import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import {
  Alert,
  Box,
  Button,
  Grid,
  IconButton,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";
import { v4 as uuidv4 } from "uuid";

interface HandoffTarget {
  id: string;
  name: string;
  description: string;
  email: string;
  phone: string;
}

interface HandoffSectionProps {
  handoffTargets: HandoffTarget[];
  setHandoffTargets: (targets: HandoffTarget[]) => void;
  disabled?: boolean;
}

const HandoffSection: React.FC<HandoffSectionProps> = ({
  handoffTargets,
  setHandoffTargets,
  disabled = false,
}) => {
  const addHandoffTarget = () => {
    setHandoffTargets([
      ...handoffTargets,
      {
        id: uuidv4(),
        name: "",
        description: "",
        email: "",
        phone: "",
      },
    ]);
  };

  const updateHandoffTarget = (
    id: string,
    field: keyof HandoffTarget,
    value: string
  ) => {
    setHandoffTargets(
      handoffTargets.map((target) =>
        target.id === id ? { ...target, [field]: value } : target
      )
    );
  };

  const removeHandoffTarget = (id: string) => {
    setHandoffTargets(handoffTargets.filter((target) => target.id !== id));
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Handoff Targets
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Define the people or departments that the agent can escalate or hand
          off conversations to. When the agent determines a handoff is needed,
          it will use the appropriate contact method based on the channel.
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Email</strong> is used for web chat, SMS, and email channel
            escalations.
            <br />
            <strong>Phone</strong> is used for voice call transfers (real phone
            calls only, not web voice).
          </Typography>
        </Alert>
      </Box>

      {handoffTargets.length === 0 ? (
        <Paper
          sx={{
            p: 3,
            textAlign: "center",
            border: 1,
            borderColor: "divider",
            borderStyle: "dashed",
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No handoff targets configured. Add people or departments that the
            agent can escalate to.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addHandoffTarget}
            disabled={disabled}
          >
            Add Handoff Target
          </Button>
        </Paper>
      ) : (
        <Box>
          {handoffTargets.map((target, index) => (
            <Paper
              key={target.id}
              sx={{
                p: 2,
                mb: 2,
                border: 1,
                borderColor: "divider",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  Handoff Target {index + 1}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => removeHandoffTarget(target.id)}
                  disabled={disabled}
                  color="error"
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Name"
                    value={target.name}
                    onChange={(e) =>
                      updateHandoffTarget(target.id, "name", e.target.value)
                    }
                    fullWidth
                    required
                    disabled={disabled}
                    placeholder="e.g., Sales Team, Technical Support, John Smith"
                    helperText="Person or department name"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="Email"
                    value={target.email}
                    onChange={(e) =>
                      updateHandoffTarget(target.id, "email", e.target.value)
                    }
                    fullWidth
                    type="email"
                    disabled={disabled}
                    placeholder="support@example.com"
                    helperText="For web, SMS, and email escalations"
                    InputProps={{
                      startAdornment: (
                        <EmailIcon
                          fontSize="small"
                          sx={{ mr: 1, color: "text.secondary" }}
                        />
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="Phone Number"
                    value={target.phone}
                    onChange={(e) =>
                      updateHandoffTarget(target.id, "phone", e.target.value)
                    }
                    fullWidth
                    disabled={disabled}
                    placeholder="+1 (555) 123-4567"
                    helperText="For voice call transfers only"
                    InputProps={{
                      startAdornment: (
                        <PhoneIcon
                          fontSize="small"
                          sx={{ mr: 1, color: "text.secondary" }}
                        />
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="When to Handoff"
                    value={target.description}
                    onChange={(e) =>
                      updateHandoffTarget(
                        target.id,
                        "description",
                        e.target.value
                      )
                    }
                    fullWidth
                    multiline
                    rows={2}
                    disabled={disabled}
                    placeholder="Describe when the agent should escalate to this target. e.g., When the customer requests to speak with a human, when technical issues require expert assistance, when billing disputes need resolution..."
                    helperText="Describe the conditions when the agent should hand off to this target"
                  />
                </Grid>
              </Grid>
            </Paper>
          ))}

          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addHandoffTarget}
            disabled={disabled}
            sx={{ mt: 1 }}
          >
            Add Another Target
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default HandoffSection;
